import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDirectBankingMiddlewareApi } from '../../context';
import utils from '../../services/utils.service';
import moment from 'moment';
import { LoadingButton } from '../../components/loadingButton';
import { AlertDialog } from '../../components/alertDialog';


function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export const NewOrder = () => {
  const { t, i18n } = useTranslation(['directBankingOrders']);
  const api = useDirectBankingMiddlewareApi();
  const history = useHistory();

  moment.locale(i18n.language);

  const [loading, setLoading] = React.useState(false);

  const [externalReference, setExternalReference] = React.useState('');
  const [amount, setAmount] = React.useState(0);
  const [urlOk, setUrlOk] = React.useState('');
  const [urlKo, setUrlKo] = React.useState('');
  const [urlCallback, setUrlCallback] = React.useState('');
  const [locale, setLocale] = React.useState(i18n.language);
  const [currencyIsoCode, setCurrencyIsoCode] = React.useState('EUR');

  const [errors, setErrors] = React.useState({
    externalReference: false,
    amount: false,
    urlOk: false,
    urlKo: false,
    locale: false,
    currencyIsoCode: false,
    urlCallback: false
  });

  const [submitError, setSubmitError] = React.useState(false);
  const [resultUrl, setResultUrl] = React.useState('');
  const [resultOrderId, setResultOrderId] = React.useState<number | null>(null);
  const [showResult, setShowResult] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const newOrder = () => {
    const validationErrors = {
      externalReference: false,
      amount: false,
      urlOk: false,
      urlKo: false,
      locale: false,
      currencyIsoCode: false,
      urlCallback: false
    };
    let hasErrors = false;
    if (externalReference.length === 0) {
      validationErrors.externalReference = true;
      hasErrors = true;
    }
    if (amount === 0) {
      validationErrors.amount = true;
      hasErrors = true;
    }
    if (urlOk.length === 0 || !isValidUrl(urlOk)) {
      validationErrors.urlOk = true;
      hasErrors = true;
    }
    if (urlKo.length === 0 || !isValidUrl(urlKo)) {
      validationErrors.urlKo = true;
      hasErrors = true;
    }
    if (urlCallback.length === 0 || !isValidUrl(urlCallback)) {
      validationErrors.urlCallback = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }

    utils.runAsync(async () => {
      setLoading(true);
      const response = await api.newOrder(
        externalReference,
        amount,
        urlOk,
        urlKo,
        currencyIsoCode,
        urlCallback,
        locale
      );
      setResultUrl(response.payment_url);
      setResultOrderId(response.order_id);
      setShowResult(true);
      setLoading(false);
    }, (e) => {
      if (e) {
        setLoading(false);
        setSubmitError(true);
      }
    });
  }

  const resetErrors = () => {
    setErrors({
      externalReference: false,
      amount: false,
      urlOk: false,
      urlKo: false,
      locale: false,
      currencyIsoCode: false,
      urlCallback: false
    });
  }

  return (
    <>
      <AlertDialog
        title={t('ERROR_CREATING_ORDER_TITLE')}
        message={t('ERROR_CREATING_ORDER_MESSAGE')}
        open={submitError}
        onClose={() => setSubmitError(false)}
      />
      <Grid container spacing={2}>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12}>
            <Paper>
              <Grid container>
                <Grid item xs={12}>
                  <Box paddingY={3} paddingX={2}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography gutterBottom variant='h5'>{t('CREATE_NEW_ORDER')}</Typography>
                      </Grid>
                      <Grid item xs={12} style={{ marginTop: 8 }}>
                        <Typography gutterBottom>
                          {t('CREATE_NEW_ORDER_DESCRIPTION')}
                        </Typography>
                      </Grid>
                      <Grid item container spacing={2} style={{ marginTop: 4 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('EXTERNAL_REFERENCE')}
                            value={externalReference}
                            error={errors.externalReference}
                            helperText={errors.externalReference ? t('REQUIRED') : ''}
                            onChange={(e) => { setExternalReference(e.target.value); resetErrors(); }}
                            type='text'
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label={t('LOCALE')}
                            value={locale}
                            error={errors.locale}
                            helperText={errors.locale ? t('REQUIRED') : ''}
                            onChange={(e) => { setLocale(e.target.value); resetErrors(); }}
                            fullWidth
                          >
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="es">Español</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('AMOUNT')}
                            value={amount}
                            error={errors.amount}
                            helperText={errors.amount ? t('REQUIRED') : ''}
                            onChange={(e) => { setAmount(parseFloat(e.target.value)); resetErrors(); }}
                            type='text'
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('CURRENCY_ISO_CODE')}
                            value={currencyIsoCode}
                            error={errors.currencyIsoCode}
                            helperText={errors.currencyIsoCode ? t('REQUIRED') : ''}
                            onChange={(e) => { setCurrencyIsoCode(e.target.value); resetErrors(); }}
                            type='text'
                            disabled={true}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('URL_OK')}
                            value={urlOk}
                            error={errors.urlOk}
                            helperText={
                              !urlOk
                                ? t('REQUIRED')
                                : !isValidUrl(urlOk)
                                ? t('INVALID_URL')
                                : ''
                            }
                            onChange={(e) => { setUrlOk(e.target.value); resetErrors(); }}
                            type='text'
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('URL_KO')}
                            value={urlKo}
                            error={errors.urlKo}
                            helperText={
                              !urlKo
                                ? t('REQUIRED')
                                : !isValidUrl(urlKo)
                                ? t('INVALID_URL')
                                : ''
                            }
                            onChange={(e) => { setUrlKo(e.target.value); resetErrors(); }}
                            type='text'
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t('URL_CALLBACK')}
                            value={urlCallback}
                            error={errors.urlCallback}
                            helperText={
                              !urlCallback
                                ? t('REQUIRED')
                                : !isValidUrl(urlCallback)
                                ? t('INVALID_URL')
                                : ''
                            }
                            onChange={(e) => { setUrlCallback(e.target.value); resetErrors(); }}
                            type='text'
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item container justify='flex-end' xs={12}>
                  <Box marginTop={6} paddingY={1} paddingX={2}>
                    <Grid container>
                      <Grid item xs={12}>
                        <LoadingButton loading={loading} variant='contained' color='primary' onClick={() => { newOrder(); }}>
                          {t('CREATE')}
                        </LoadingButton>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('ORDER_CREATED')}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {t('ORDER_CREATED_DESCRIPTION')}
          </Typography>
          <Box display="flex" alignItems="center" mt={2} mb={2}>
            <TextField
              value={resultUrl}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
            />
            <Button
              style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
              variant="outlined"
              size="small"
              onClick={async () => {
                await navigator.clipboard.writeText(resultUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {t('COPY_URL')}
            </Button>
          </Box>
          {copied && (
            <Typography variant="caption" color="primary">
              {t('URL_COPIED')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResult(false)} color="default">
            {t('CLOSE')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!resultOrderId}
            onClick={() => {
              if (resultOrderId) {
                history.push(`/orders/${resultOrderId}`);
              }
            }}
          >
            {t('GO_TO_THE_ORDER')}
          </Button>
        </DialogActions>
      </Dialog>
    </>

  )
}